import { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { company } from '../mock';

const inquiryTypes = ['Equipment Leasing', 'Working Capital Loan', 'Sale-Leaseback', 'Mortgage Refinancing', 'Vendor Partnership', 'General Question'];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', amount: '', inquiry: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.inquiry) {
      toast({ title: 'Missing info', description: 'Please add your name, email and inquiry type.', variant: 'destructive' });
      return;
    }
    const stored = JSON.parse(localStorage.getItem('foxstone_inquiries') || '[]');
    stored.push({ ...form, ts: Date.now() });
    localStorage.setItem('foxstone_inquiries', JSON.stringify(stored));
    toast({ title: 'Request received', description: 'A Foxstone advisor will be in touch within one business day.' });
    setSubmitted(true);
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's structure your next deal"
        subtitle="Tell us about your business and what you need to finance. A dedicated Foxstone advisor will respond same business day."
        image="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&q=80"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-fx-gold font-semibold">Reach Us</p>
              <h2 className="font-display text-3xl text-fx-navy font-bold mt-3">Our advisors are ready</h2>
              <div className="fx-section-divider mt-5" />
              <p className="mt-5 text-slate-600">Pick up the phone, drop us an email, or use the form. Every inquiry receives a same-day response from a real advisor.</p>
            </div>
            {[{ icon: Phone, label: 'Call us', value: company.phone, href: `tel:${company.phone}` },
              { icon: Mail, label: 'Email us', value: company.email, href: `mailto:${company.email}` },
              { icon: MapPin, label: 'Head office', value: company.address }].map((c, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-lg bg-fx-cream border border-fx-gold/25">
                <div className="w-11 h-11 rounded-md bg-fx-navy text-fx-gold flex items-center justify-center shrink-0"><c.icon className="w-5 h-5" /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="text-fx-navy font-semibold hover:text-fx-gold">{c.value}</a>
                  ) : (
                    <p className="text-fx-navy font-semibold leading-snug">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-lg p-8 lg:p-10 shadow-[0_30px_60px_-40px_rgba(10,37,64,0.4)]">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto rounded-full bg-fx-gold/15 flex items-center justify-center"><CheckCircle2 className="w-8 h-8 text-fx-gold" /></div>
                  <h3 className="font-display text-2xl text-fx-navy font-bold mt-5">Thank you, {form.name.split(' ')[0]}.</h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">Your inquiry has been received. A Foxstone advisor will reach out within one business day at {form.email}.</p>
                  <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', company: '', amount: '', inquiry: '', message: '' }); }} className="mt-7 bg-fx-navy hover:bg-fx-navy-deep text-white rounded-sm">Submit another inquiry</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <p className="text-xs uppercase tracking-[0.28em] text-fx-gold font-semibold">Contact Us</p>
                    <h3 className="font-display text-2xl text-fx-navy font-bold mt-1">Tell us about your business</h3>
                  </div>
                  <Field label="Full name"><Input value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Jane Smith" /></Field>
                  <Field label="Email address"><Input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="jane@business.ca" /></Field>
                  <Field label="Phone"><Input value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="(555) 123-4567" /></Field>
                  <Field label="Company"><Input value={form.company} onChange={(e) => handleChange('company', e.target.value)} placeholder="Your business" /></Field>
                  <Field label="Amount needed"><Input value={form.amount} onChange={(e) => handleChange('amount', e.target.value)} placeholder="$75,000" /></Field>
                  <Field label="Inquiry type">
                    <Select value={form.inquiry} onValueChange={(v) => handleChange('inquiry', v)}>
                      <SelectTrigger className="h-10"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent>{inquiryTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">How can we help?</Label>
                    <Textarea value={form.message} onChange={(e) => handleChange('message', e.target.value)} placeholder="Tell us about the equipment, timeline, and any other relevant details." className="mt-2 min-h-[120px]" />
                  </div>
                  <div className="sm:col-span-2">
                    <Button type="submit" className="w-full sm:w-auto bg-fx-navy hover:bg-fx-navy-deep text-white rounded-sm h-12 px-7"><Send className="w-4 h-4 mr-2" />Submit Inquiry</Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
